import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AiProviderTile } from "@vd/ui";
import { useEffect, useState } from "react";
import { overviewQuery } from "../../api/admin";
import { CONNECTOR_PROVIDERS, connectorQuery, PROVIDER_META, providerOf } from "../../api/connector";
import { integrationsApi } from "../../api/integrations";
import { onboardingQuery } from "../../api/onboarding";
import { isAdmin as isAdminOf, requireAdmin, useMe } from "../../auth/useMe";
import { AgentSetupCard } from "../../components/getstarted/AgentSetupCard";
import { GetStartedCard } from "../../components/getstarted/GetStartedCard";
import { MemberIntegrations } from "../../components/integrations/MemberIntegrations";
import { GuidedOnboarding } from "../../components/onboarding/GuidedOnboarding";
import { Badge, Card, Dot, ErrorState, Loading, PageHeader, Stat } from "../../components/ui";
import { fmtNumber, fmtPercent, relTime } from "../../lib/format";
import { HEALTH, PROVIDER_LABEL } from "../../lib/labels";
import { Link } from "../../lib/navigate";

export const Route = createFileRoute("/admin/")({
  staticData: { crumb: { label: "Home", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchQuery(overviewQuery),
  component: OverviewPage,
});

/** Claude and ChatGPT for reps: each On or Off, like the provider rows. Absent if it can't load. */
function ConnectorLine() {
  const v = useQuery(connectorQuery).data;
  if (!v) return null;
  return (
    <>
      {CONNECTOR_PROVIDERS.map((p) => {
        const on = providerOf(v, p).enabled;
        return (
          <li key={p} className="flex items-center gap-3 px-4 py-3">
            <Dot tone={on ? "ok" : "neutral"} />
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <AiProviderTile provider={p} size={24} />
              <span className="truncate text-ui font-medium">{PROVIDER_META[p].name}</span>
            </span>
            <Badge tone={on ? "ok" : "neutral"}>{on ? "On" : "Off"}</Badge>
          </li>
        );
      })}
    </>
  );
}

/** The guided onboarding over Home (admins only). It opens when the server says so and then
 * stays until the person leaves it (Close for now or Go to Home), even when finishing the last
 * step makes the server's view closed, so the Done screen shows. */
function Guided() {
  const ob = useQuery(onboardingQuery);
  const [showing, setShowing] = useState(false);
  // Hidden at once on leaving; a later answer that opens it (Resume) shows it again.
  const [closedAt, setClosedAt] = useState(0);
  const v = ob.data;
  const opens = !!v?.guided.open && ob.dataUpdatedAt > closedAt;
  useEffect(() => {
    if (opens) setShowing(true);
  }, [opens]);
  if (!v || !(showing || opens)) return null;
  return (
    <GuidedOnboarding
      data={v}
      onClosed={() => {
        setShowing(false);
        setClosedAt(Date.now());
      }}
    />
  );
}

function OverviewPage() {
  const me = useMe();
  const q = useQuery(overviewQuery);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const o = q.data;
  const connectRate = o.calls_today ? o.connected_today / o.calls_today : 0;
  const isAdmin = isAdminOf(me);

  return (
    <>
      <PageHeader title="Home">
        <span className="text-ui text-ink-2">Today</span>
      </PageHeader>
      {isAdmin ? <GetStartedCard /> : null}
      {isAdmin ? <Guided /> : null}
      {isAdmin ? <AgentSetupCard /> : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Calls" value={fmtNumber(o.calls_today)} />
        <Stat
          label="Connect rate"
          value={o.calls_today ? fmtPercent(connectRate) : "None yet"}
          sub={`${o.connected_today} connected`}
        />
        <Stat label="Talk time" value={`${fmtNumber(o.talk_minutes)} min`} />
        <Stat
          label="Drops"
          value={fmtNumber(o.drops_today)}
          tone={o.drops_today > 0 ? "danger" : undefined}
        />
        <Stat
          label="Errors"
          value={fmtNumber(o.errors_today)}
          tone={o.errors_today > 0 ? "warn" : undefined}
        />
        <Stat label="Active users" value={fmtNumber(o.active_users)} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* Only the providers the company owns (ADR 0050); none, no card. */}
        {o.providers.length ? (
          <Card
            title="Providers"
            actions={
              isAdmin ? (
                <Link to="/admin/integrations" className="text-ui text-accent-ink hover:underline">
                  Manage
                </Link>
              ) : null
            }
          >
            <ul className="divide-y divide-hairline">
              {o.providers.map((p) => {
                const h = HEALTH[p.health.status];
                return (
                  <li key={p.name} className="flex items-center gap-3 px-4 py-3">
                    <Dot tone={h.tone} />
                    <span className="w-20 text-ui font-medium">{PROVIDER_LABEL[p.name]}</span>
                    <span className="min-w-0 flex-1 truncate text-ui text-ink-2">
                      {p.health.message ??
                        (p.health.checked_at ? `Checked ${relTime(p.health.checked_at).toLowerCase()}` : "")}
                    </span>
                    <Badge tone={h.tone}>{h.label}</Badge>
                  </li>
                );
              })}
              {isAdmin ? <ConnectorLine /> : null}
            </ul>
          </Card>
        ) : null}
        <Card
          title="Integrations"
          actions={
            isAdmin ? (
              <Link to="/admin/integrations" className="text-ui text-accent-ink hover:underline">
                Manage
              </Link>
            ) : null
          }
        >
          <MemberIntegrations rows={o.integrations ?? []} onRemind={(id) => integrationsApi.remind(id)} />
          {o.pending_deliveries > 0 ? (
            <p className="border-t border-hairline px-4 py-2.5 text-ui text-ink-2">
              {o.pending_deliveries} call {o.pending_deliveries === 1 ? "note is" : "notes are"} waiting to
              post to the CRM.
            </p>
          ) : null}
        </Card>
      </div>
    </>
  );
}
